import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Trash2, UserMinus, UserPlus2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BetaFeatureRow, BetaUser } from "./types";
import { isBetaCatalogKey } from "@shared/beta-catalog";

export function BetaFeaturesTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const featuresQuery = useQuery<BetaFeatureRow[]>({
    queryKey: ["/api/admin/beta-features"],
    queryFn: async () => {
      const res = await fetch("/api/admin/beta-features");
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });
  const features = (featuresQuery.data ?? []).filter(feature => isBetaCatalogKey(feature.key));

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selectedFeature = features.find((f) => f.key === selectedKey) ?? null;

  const usersQuery = useQuery<BetaUser[]>({
    queryKey: ["/api/admin/beta-features", selectedKey, "users"],
    queryFn: async () => {
      if (!selectedKey) return [];
      const res = await fetch(`/api/admin/beta-features/${selectedKey}/users`);
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
    enabled: !!selectedKey,
  });

  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ key: "", name: "", description: "", isActive: true, allowSelfOptIn: false });

  const createMutation = useMutation({
    mutationFn: async (data: typeof createForm) => {
      await apiRequest("POST", "/api/admin/beta-features", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/beta-features"] });
      setCreateDialog(false);
      setCreateForm({ key: "", name: "", description: "", isActive: true, allowSelfOptIn: false });
      toast({ title: "Beta feature created" });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ key, isActive }: { key: string; isActive: boolean }) => {
      await apiRequest("PATCH", `/api/admin/beta-features/${key}`, { isActive });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/beta-features"] }),
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const toggleOptInMutation = useMutation({
    mutationFn: async ({ key, allowSelfOptIn }: { key: string; allowSelfOptIn: boolean }) => {
      await apiRequest("PATCH", `/api/admin/beta-features/${key}`, { allowSelfOptIn });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/beta-features"] }),
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (key: string) => {
      await apiRequest("DELETE", `/api/admin/beta-features/${key}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/beta-features"] });
      if (selectedKey === deleteMutation.variables) setSelectedKey(null);
      toast({ title: "Beta feature deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const [searchQuery, setSearchQuery] = useState("");
  const searchResultsQuery = useQuery<Array<{ id: number; email: string; name: string | null }>>({
    queryKey: ["/api/admin/beta-search-users", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const res = await fetch(`/api/admin/beta-search-users?q=${encodeURIComponent(searchQuery)}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: searchQuery.length >= 2,
  });

  const grantMutation = useMutation({
    mutationFn: async ({ featureKey, userId }: { featureKey: string; userId: number }) => {
      await apiRequest("POST", `/api/admin/beta-features/${featureKey}/users`, { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/beta-features", selectedKey, "users"] });
      setSearchQuery("");
      toast({ title: "Access granted" });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ featureKey, userId }: { featureKey: string; userId: number }) => {
      await apiRequest("DELETE", `/api/admin/beta-features/${featureKey}/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/beta-features", selectedKey, "users"] });
      toast({ title: "Access revoked" });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Beta Features</h2>
          <p className="text-sm text-muted-foreground">Manage feature flags and user access for beta features.</p>
          <p className="text-sm text-muted-foreground">Member Cleaning, Owner Portal and Quo Messaging are generally available. AI Drafting access is managed by plan in Menu Tier Locks, not beta grants.</p>
        </div>
        <Button size="sm" onClick={() => setCreateDialog(true)} data-testid="button-create-beta-feature" data-tour="admin-beta-features-new">
          <Plus className="h-4 w-4 mr-1.5" />
          New Feature
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Feature list */}
        <Card className="lg:col-span-1" data-tour="admin-beta-features-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Feature Flags</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {featuresQuery.isLoading ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : features.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No beta features yet.
              </div>
            ) : (
              <div className="divide-y">
                {features.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setSelectedKey(f.key === selectedKey ? null : f.key)}
                    className={`w-full text-left px-4 py-3 hover:bg-accent transition-colors ${selectedKey === f.key ? "bg-accent" : ""}`}
                    data-testid={`beta-feature-row-${f.key}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{f.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{f.key}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant={f.isActive ? "default" : "outline"} className="text-[10px] px-1.5 py-0">
                          {f.isActive ? "Active" : "Off"}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{f.userCount}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail panel */}
        <Card className="lg:col-span-2">
          {!selectedFeature ? (
            <CardContent className="flex items-center justify-center h-40 text-sm text-muted-foreground">
              Select a feature to manage users and settings.
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">{selectedFeature.name}</CardTitle>
                    <CardDescription className="font-mono text-xs mt-0.5">{selectedFeature.key}</CardDescription>
                    {selectedFeature.description && (
                      <p className="text-sm text-muted-foreground mt-1.5">{selectedFeature.description}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => { if (confirm(`Delete "${selectedFeature.name}"? This removes all user access.`)) deleteMutation.mutate(selectedFeature.key); }}
                    disabled={deleteMutation.isPending || selectedFeature.gateEnabled}
                    aria-label="Delete beta feature"
                    data-testid="button-delete-beta-feature"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {selectedFeature.gateEnabled && (
                  <p className="text-sm text-muted-foreground">
                    Deletion is blocked while this feature has an enabled gate. Disable it in Coming Soon settings first.
                    Disabling the gate opens the section to users without a bypass grant; deleting this beta removes all its user grants.
                  </p>
                )}
                <div className="flex items-center gap-4 pt-2 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={selectedFeature.isActive}
                      onCheckedChange={(v) => toggleActiveMutation.mutate({ key: selectedFeature.key, isActive: v })}
                      disabled={toggleActiveMutation.isPending}
                      data-testid="switch-beta-active"
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={selectedFeature.allowSelfOptIn}
                      onCheckedChange={(v) => toggleOptInMutation.mutate({ key: selectedFeature.key, allowSelfOptIn: v })}
                      disabled={toggleOptInMutation.isPending}
                      data-testid="switch-beta-opt-in"
                    />
                    <span>Allow self-opt-in</span>
                  </label>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Users with access ({usersQuery.data?.length ?? 0})</h4>
                </div>

                {/* Grant access search */}
                <div className="relative">
                  <Input
                    placeholder="Search users to grant access..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="text-sm"
                    data-testid="input-beta-user-search"
                  />
                  {searchResultsQuery.data && searchResultsQuery.data.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-10 mt-1 rounded-md border bg-background shadow-md">
                      {searchResultsQuery.data.map((u) => (
                        <button
                          key={u.id}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-accent"
                          onClick={() => grantMutation.mutate({ featureKey: selectedFeature.key, userId: u.id })}
                          disabled={grantMutation.isPending}
                          data-testid={`button-grant-beta-user-${u.id}`}
                        >
                          <div className="min-w-0">
                            <div className="font-medium truncate">{u.email}</div>
                            {u.name && <div className="text-xs text-muted-foreground truncate">{u.name}</div>}
                          </div>
                          <UserPlus2 className="h-4 w-4 text-blue-600 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Users list */}
                {usersQuery.isLoading ? (
                  <div className="space-y-2">
                    {[1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : (usersQuery.data?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No users have access yet.</p>
                ) : (
                  <div className="divide-y rounded-md border overflow-hidden">
                    {usersQuery.data?.map((u) => (
                      <div key={u.id} className="flex items-center justify-between gap-2 px-3 py-2.5" data-testid={`beta-user-row-${u.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{u.email}</div>
                          {u.name && <div className="text-xs text-muted-foreground truncate">{u.name}</div>}
                          <div className="text-xs text-muted-foreground">{new Date(u.grantedAt).toLocaleDateString()}</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => revokeMutation.mutate({ featureKey: selectedFeature.key, userId: u.id })}
                          disabled={revokeMutation.isPending}
                          data-testid={`button-revoke-beta-user-${u.id}`}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent data-testid="dialog-create-beta-feature">
          <DialogHeader>
            <DialogTitle>New Beta Feature</DialogTitle>
            <DialogDescription>Create a new feature flag for beta access control.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate(createForm);
            }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="beta-key">Key (unique, slug-style)</Label>
              <Input
                id="beta-key"
                placeholder="e.g. devices"
                value={createForm.key}
                onChange={(e) => setCreateForm((f) => ({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, "") }))}
                required
                className="mt-1 font-mono"
                data-testid="input-beta-key"
              />
            </div>
            <div>
              <Label htmlFor="beta-name">Display Name</Label>
              <Input
                id="beta-name"
                placeholder="e.g. New Feature Preview"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                required
                className="mt-1"
                data-testid="input-beta-name"
              />
            </div>
            <div>
              <Label htmlFor="beta-desc">Description (optional)</Label>
              <Textarea
                id="beta-desc"
                placeholder="What does this feature do?"
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                className="mt-1"
                data-testid="input-beta-description"
              />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={createForm.isActive}
                  onCheckedChange={(v) => setCreateForm((f) => ({ ...f, isActive: v }))}
                  data-testid="switch-create-beta-active"
                />
                <span>Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={createForm.allowSelfOptIn}
                  onCheckedChange={(v) => setCreateForm((f) => ({ ...f, allowSelfOptIn: v }))}
                  data-testid="switch-create-beta-opt-in"
                />
                <span>Allow self-opt-in</span>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialog(false)} data-testid="button-cancel-create-beta">Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending} data-testid="button-save-create-beta">
                {createMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
